import type { MediaEntry, MediaKind } from '@roomtreatment/core';
import { mediaAsMountable } from '@roomtreatment/core';
import { inferSurface, type SurfaceFrame } from './panelSurface';

/**
 * Which surface a media object hangs on, and which ones it is allowed to.
 *
 * A TV is inferred exactly the way a panel is — from its own transform, so a
 * shared or legacy document reads the same as a hand-placed one. A projector
 * is NOT, and cannot be:
 *
 * `inferSurface` scores candidates mostly on `facing · normal`, and a
 * projector is yawed to throw at its screen, so its local +Z points
 * HORIZONTALLY at a wall. Inferring it lands on that wall every time — the
 * one surface it is definitely not attached to. Left alone that makes a
 * ceiling projector dim with the wall it points at, and makes a drag slide it
 * along that wall.
 *
 * So a projector's mount is not derived, it is known: a projector hangs from
 * the ceiling or it sits on something (`detached`). There is no third case,
 * and therefore nothing to infer and no picker to offer.
 */
export function mediaHostSurface(
  entry: MediaEntry, surfaces: SurfaceFrame[],
): SurfaceFrame | null {
  if (entry.detached) return null;
  if (entry.kind === 'projector') {
    return surfaces.find((s) => s.ref.kind === 'ceiling') ?? null;
  }
  // Restricted to the surfaces a TV may actually hang on, so inference cannot
  // pick a floor or a corner however the entry happens to be rotated.
  const options = mediaSurfaceOptions(entry.kind, surfaces);
  return options.length ? inferSurface(mediaAsMountable(entry), options) : null;
}

/** Surfaces this kind of media may mount to. A TV goes on a wall; anywhere
 *  else it is free-standing, which is `detached` rather than a surface. */
export function mediaSurfaceOptions(
  kind: MediaKind, surfaces: SurfaceFrame[],
): SurfaceFrame[] {
  return kind === 'projector'
    ? surfaces.filter((s) => s.ref.kind === 'ceiling')
    : surfaces.filter((s) => s.ref.kind === 'wall');
}
